/**
 * BENTO SCENES (render puro) — mini-widgets de CONTENIDO para tiles de bento.
 *
 * NO importa render.js/counter.js/store (lo usa bento.js ← registry.js ← render.js;
 * un import a render.js crearía ciclo). La animación + el registro viven en
 * animations/bento-scenes-anim.js (cargado desde el entry, como counter.js).
 *
 * La IA elige `type` de la LISTA BLANCA (SCENE_CATALOG) y aporta los datos; nunca
 * SVG/markup libre. El contenido se emite COMPLETO (legible sin JS); la animación
 * solo lo "re-juega". Cada arquetipo rellena el tile (sin huecos).
 *
 * Añadir arquetipo = 1 entrada en SCENE_CATALOG + 1 caso en renderScene
 * (+ estilo en site.css, + whitelist en llm_sanitizers._fix_bento_scenes).
 */

// Lista blanca. El sanitizer de Python (llm_sanitizers._fix_bento_scenes) la espeja.
export const SCENE_CATALOG = {
    counter:  { label: 'Contador' },        // cifra que sube
    ticker:   { label: 'Etiquetas' },       // tags en bucle
    terminal: { label: 'Terminal' },        // teclea líneas (logs/pasos/código)
    menu:     { label: 'Lista precios' },   // filas etiqueta · valor (carta, specs)
    rating:   { label: 'Valoración' },      // estrellas + nota + nº reseñas
    gauge:    { label: 'Anillo %' },         // progreso circular
    bars:     { label: 'Gráfico barras' },  // mini bar chart
    avatars:  { label: 'Comunidad' },       // avatares + "+2K clientes"
    map:      { label: 'Ubicación' },        // pin pulsante + dirección
    hours:    { label: 'Horario' },          // estado abierto/cerrado
    quote:    { label: 'Cita' },             // testimonio breve
};

function esc(s) {
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

// Extrae un número de un string tipo "98%", "4.9", "+12K" → float (o NaN).
function num(v) {
    const m = String(v == null ? '' : v).replace(',', '.').match(/-?\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : NaN;
}
function clampN(n, a, b) { return Math.max(a, Math.min(b, n)); }

const STAR = '<svg viewBox="0 0 24 24" class="rt-star"><path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77 5.82 21l1.18-6.88-5-4.87 7.1-1.01L12 2z"/></svg>';

/** Devuelve el HTML de una escena, o '' si no es válida (degradación elegante). */
export function renderScene(scene) {
    if (!scene || typeof scene !== 'object' || !SCENE_CATALOG[scene.type]) return '';
    const t = scene.type;

    if (t === 'counter') {
        if (!scene.value) return '';
        return `<div class="bento-scene scene-counter" data-scene="counter">
            <span class="scene-num">${esc(scene.value)}</span>
            ${scene.label ? `<span class="scene-label">${esc(scene.label)}</span>` : ''}
        </div>`;
    }

    if (t === 'ticker') {
        const tags = (Array.isArray(scene.tags) ? scene.tags : []).filter(Boolean).slice(0, 8);
        if (!tags.length) return '';
        const row = tags.map(x => `<span class="tk-tag">${esc(x)}</span>`).join('');
        return `<div class="bento-scene scene-ticker" data-scene="ticker"><div class="tk-track">${row}${row}</div></div>`;
    }

    if (t === 'terminal') {
        const lines = (Array.isArray(scene.lines) ? scene.lines : []).filter(Boolean).slice(0, 6);
        if (!lines.length) return '';
        const body = lines.map(l => `<span class="term-line" data-line="${esc(l)}">${esc(l)}</span>`).join('');
        return `<div class="bento-scene scene-terminal" data-scene="terminal">
            <div class="term-bar"><i></i><i></i><i></i></div>
            <div class="term-body">${body}</div>
        </div>`;
    }

    if (t === 'menu') {
        const rows = (Array.isArray(scene.rows) ? scene.rows : []).filter(r => r && (r.label || r.value)).slice(0, 6);
        if (!rows.length) return '';
        const body = rows.map((r, i) => `<div class="mn-row" style="--mn-i:${i}">
            <span class="mn-label">${esc(r.label || '')}</span>
            <span class="mn-dots"></span>
            <span class="mn-val">${esc(r.value || '')}</span>
        </div>`).join('');
        return `<div class="bento-scene scene-menu" data-scene="menu">${body}</div>`;
    }

    if (t === 'rating') {
        const max = clampN(num(scene.max) || 5, 1, 10);
        const val = clampN(isNaN(num(scene.value)) ? max : num(scene.value), 0, max);
        const full = Math.round((val / max) * 5);
        const stars = Array.from({ length: 5 }, (_, i) => `<span class="${i < full ? 'on' : ''}">${STAR}</span>`).join('');
        return `<div class="bento-scene scene-rating" data-scene="rating">
            <span class="rt-val">${esc(scene.value || val)}</span>
            <span class="rt-stars">${stars}</span>
            ${scene.count ? `<span class="rt-count">${esc(scene.count)}</span>` : ''}
        </div>`;
    }

    if (t === 'gauge') {
        const pct = clampN(isNaN(num(scene.value)) ? 0 : num(scene.value), 0, 100);
        const C = 327; // 2·π·52
        const off = C * (1 - pct / 100);
        return `<div class="bento-scene scene-gauge" data-scene="gauge" data-off="${off.toFixed(1)}">
            <svg class="gg-svg" viewBox="0 0 120 120">
                <circle class="gg-track" cx="60" cy="60" r="52"></circle>
                <circle class="gg-fill" cx="60" cy="60" r="52" style="stroke-dasharray:${C};stroke-dashoffset:${off.toFixed(1)};"></circle>
            </svg>
            <span class="gg-center">${esc(scene.value || pct)}${scene.unit ? esc(scene.unit) : ''}</span>
            ${scene.label ? `<span class="gg-label">${esc(scene.label)}</span>` : ''}
        </div>`;
    }

    if (t === 'bars') {
        const bars = (Array.isArray(scene.bars) ? scene.bars : []).filter(Boolean).slice(0, 7);
        if (!bars.length) return '';
        const vals = bars.map(b => clampN(isNaN(num(b.value)) ? num(b) : num(b.value), 4, 100));
        const body = vals.map((v, i) => `<span class="br" style="--h:${v}%;--br-i:${i}" title="${esc(bars[i].label || '')}"></span>`).join('');
        const labels = bars.some(b => b && b.label)
            ? `<div class="br-labels">${bars.map(b => `<span>${esc((b && b.label) || '')}</span>`).join('')}</div>` : '';
        return `<div class="bento-scene scene-bars" data-scene="bars"><div class="br-track">${body}</div>${labels}</div>`;
    }

    if (t === 'avatars') {
        const faces = clampN(parseInt(scene.faces, 10) || 4, 1, 6);
        const dots = Array.from({ length: faces }, (_, i) => `<span class="av-face" style="--av-i:${i}"></span>`).join('');
        return `<div class="bento-scene scene-avatars" data-scene="avatars">
            <div class="av-stack">${dots}</div>
            <div class="av-text">
                ${scene.count ? `<span class="av-count">${esc(scene.count)}</span>` : ''}
                ${scene.label ? `<span class="av-label">${esc(scene.label)}</span>` : ''}
            </div>
        </div>`;
    }

    if (t === 'map') {
        return `<div class="bento-scene scene-map" data-scene="map">
            <svg class="mp-svg" viewBox="0 0 120 80" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                <path d="M0 22 H120 M0 44 H120 M0 64 H120 M24 0 V80 M60 0 V80 M92 0 V80" class="mp-grid"/>
                <path d="M-4 30 C30 18 38 50 70 40 C96 32 104 56 124 48" class="mp-road"/>
            </svg>
            <span class="mp-pin"><span class="mp-pulse"></span></span>
            ${scene.label ? `<span class="mp-label">${esc(scene.label)}</span>` : ''}
        </div>`;
    }

    if (t === 'hours') {
        const open = String(scene.status || '').toLowerCase().match(/abie|open|24/) ? 'open' : '';
        return `<div class="bento-scene scene-hours ${open}" data-scene="hours">
            <span class="hr-badge"><span class="hr-dot"></span>${esc(scene.status || 'Abierto')}</span>
            ${scene.detail ? `<span class="hr-detail">${esc(scene.detail)}</span>` : ''}
        </div>`;
    }

    if (t === 'quote') {
        if (!scene.text) return '';
        return `<div class="bento-scene scene-quote" data-scene="quote">
            <span class="qt-mark">&#8220;</span>
            <p class="qt-text">${esc(scene.text)}</p>
            ${scene.author ? `<span class="qt-author">${esc(scene.author)}</span>` : ''}
        </div>`;
    }

    return '';
}
